import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { EntityKeys, QueryType, getMutation } from 'utils/reactQuery'
import { _getUrl, useAPI } from 'utils/API'

import API from 'utils/API/API'
import axios, { AxiosResponse } from 'axios'
import { CommonStatusCode } from 'constants/API'
import { FeeDTO } from 'models/fee'
import { StatusResponse } from 'models/redux'
import { isIneligibleCreativeSelectionError } from 'utils/typeguards'
import { logger } from 'utils/logging'
import { useAuth0 } from 'utils/auth'

enum Endpoints {
  ADMIN_REMOVE_CREATIVE = '/assignment/{assignmentId}/removeCreative',
  ADMIN_ONBOARD_CREATIVE = '/user/creative',
  ADMIN_RESEND_CREATIVE_INVITATION = '/user/resendInvitation',
  ADMIN_SELECT_CREATIVE = '/assignment/{assignmentId}/creative/{creativeId}/select',
}

// MUTATIONS

interface AdminRemoveCreativePayload {
  dealId: number
  assignmentId: string
}

interface AdminSelectCreativePayload {
  dealId: number
  assignmentId: string
  creativeId: string
  remuneration: FeeDTO,
  transportRemuneration: FeeDTO,
  bonus: FeeDTO,
  /**
   * Justification for pairing a creative that failed eligibility rules (ISSUE-2946). Mandatory only
   * after the backend has rejected the selection with the typed 422; ignored when the creative is
   * eligible. Blank counts as absent server-side, so it is trimmed and omitted rather than sent empty.
   */
  overrideReason?: string
}

interface AdminOnboardCreativePayload {
  email: string,
  language: string,
  timezone: string,
}

export function useAdminRemoveCreative() {
  const api = useAPI<Endpoints>()
  return getMutation<AxiosResponse<StatusResponse>, AdminRemoveCreativePayload>(
    ({ assignmentId }) => api.put(
      Endpoints.ADMIN_REMOVE_CREATIVE,
      { assignmentId },
      {},
      true
    ),
    (client, { dealId, assignmentId }) => {
      client.invalidateQueries({ queryKey: [EntityKeys.ORDER, QueryType.GET_ONE, { orderId: dealId }] })
      client.invalidateQueries({ queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET_ONE, { assignmentId }] })
      logAnalyticsEvent(AnalyticsEvent.CREATIVE_REMOVED_BY_ADMIN, { assignmentId })
    }
  )
}

/**
 * Admin manual creative selection.
 *
 * Calls `API.post` directly rather than `useAPI` (which is otherwise the default) for one reason:
 * only the internal config carries `ignoreErrorFactory`, and `useAPI` hardcodes `{ endpoint }`.
 * With the eligibility gate on (ISSUE-2946), a 422 listing failed rules is a **normal product
 * outcome** the admin resolves by typing a justification — not an incident. Reporting it would also
 * shred the Sentry issue list: `API.logError` appends the response `message` to the fingerprint, and
 * that message interpolates the creative's UUID, so every rejected creative would open its own
 * issue. The suppression matches on payload *shape*, not on `status === 422`, so the blacklist 422
 * that shares the status keeps reporting. Same precedent as `consent.query.ts`'s expected 409.
 */
export function useAdminSelectCreative() {
  const { roles } = useAuth0()

  return getMutation<AxiosResponse<StatusResponse>, AdminSelectCreativePayload>(
    ({ creativeId, assignmentId, transportRemuneration, bonus, remuneration, overrideReason }) => {
      const trimmedOverrideReason = overrideReason?.trim()

      return API.post<StatusResponse>(
        _getUrl(Endpoints.ADMIN_SELECT_CREATIVE, { assignmentId, creativeId }),
        {
          transportRemuneration,
          bonus,
          customBaseRemuneration: remuneration,
          // Omitted when blank: the backend treats a blank reason as absent and answers the same 422
          ...(trimmedOverrideReason && { overrideReason: trimmedOverrideReason }),
        },
        { headers: { Accept: API.decideRoleMimeType(roles) } },
        {
          // The un-interpolated template, so the Sentry fingerprint does not fragment per assignment
          endpoint: Endpoints.ADMIN_SELECT_CREATIVE,
          ignoreErrorFactory: isIneligibleCreativeSelectionError,
        }
      )
    },
    (client, { creativeId, assignmentId, transportRemuneration, bonus, remuneration, dealId, overrideReason }) => {
      client.invalidateQueries({ queryKey: [EntityKeys.ORDER, QueryType.GET_ONE, { orderId: dealId }] })
      client.invalidateQueries({ queryKey: [EntityKeys.ASSIGNMENT, QueryType.GET_ONE, { assignmentId }] })
      logAnalyticsEvent(AnalyticsEvent.CREATIVE_SELECTED_BY_ADMIN, {
        assignmentId,
        creativeId,
        remuneration,
        transportRemuneration,
        bonus,
        // Boolean only — the reason is admin free text and may carry personal data, and this event
        // fans out to five analytics providers. It travels in the request body and nowhere else.
        isEligibilityOverride: !!overrideReason?.trim(),
      })
    },
    {
      // The suppression above removes this path's only Sentry reporting, so a refused selection would
      // otherwise be completely silent once the backend flag is on — no rejection rate, no visibility
      // of which rules actually fire, and no positive evidence the rollout took effect. Both messages
      // below are STATIC, so each collapses to a single Sentry group; everything variable rides in
      // `metadata`. Rule codes and categories are backend enum strings, never the admin's free text.
      onError: (error, { assignmentId, creativeId }) => {
        if (isIneligibleCreativeSelectionError(error)) {
          const failedRules = error.response?.data.ineligibleAssignments.flatMap((entry) => entry.failedRules) ?? []

          logger.warn('Admin creative selection refused by eligibility gate', undefined, {
            context: {
              component: 'useAdminSelectCreative',
              action: 'select',
              metadata: {
                assignmentId,
                creativeId,
                ruleCodes: failedRules.map((rule) => rule.code),
                ruleCategories: failedRules.map((rule) => rule.category),
              },
            },
          })
          return
        }

        // Contract drift: the backend answered 422 carrying `ineligibleAssignments`, but in a shape the
        // guard refuses — so the admin is dropped back into the un-overridable state this ticket fixes.
        // Detected here rather than inside the guard, which is a pure predicate re-run on every render.
        if (!axios.isAxiosError(error) || error.response?.status !== CommonStatusCode.UNPROCESSABLE_ENTITY) return

        const body: unknown = error.response.data
        if (!!body && typeof body === 'object' && 'ineligibleAssignments' in body) {
          logger.warn('Eligibility rejection payload did not match the expected shape', undefined, {
            context: {
              component: 'useAdminSelectCreative',
              action: 'select',
              metadata: { assignmentId, creativeId },
            },
          })
        }
      },
    }
  )
}

export function useAdminOnboardCreative() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, AdminOnboardCreativePayload>(
    ({ email, language, timezone }) => api.post(
      Endpoints.ADMIN_ONBOARD_CREATIVE,
      {},
      {},
      true,
      {
        params: {
          email,
          language,
          timezone,
        },
      }
    ),
    (_, { email, language, timezone }) => {
      logAnalyticsEvent(AnalyticsEvent.CREATIVE_ONBOARDED_BY_ADMIN, {
        email,
        language,
        timezone,
      })
    }
  )
}

export function useAdminResendCreativeInvitation() {
  const api = useAPI<Endpoints>()

  return getMutation<AxiosResponse<StatusResponse>, AdminOnboardCreativePayload>(
    ({ email, language, timezone }) => api.post(
      Endpoints.ADMIN_RESEND_CREATIVE_INVITATION,
      {},
      {},
      true,
      {
        params: {
          email,
          language,
          timezone,
        },
      }
    )
  )
}
